/* eslint-disable react/jsx-filename-extension */
/* eslint-disable import/extensions */
/* eslint-disable react/prop-types */

/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Transition } from "@headlessui/react";

import Button from "../elements/Button";
import BrandIcon from "./BrandIcon";

import Services from "../data/services";
import Industries from "../data/industries";
import AboutItems from "../data/about";

// slug helper
function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^\w-]+/g, "");
}

export default function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  /* ── Scroll-aware glass header ──────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinkClass = (path, exact) => {
    const isActive = exact ? pathname === path : pathname.startsWith(path);
    return `flex items-center gap-1.5 px-4 py-2.5 text-base font-medium rounded-xl transition-all duration-200
      ${isActive
        ? "text-brand-green-600"
        : "text-surface-800 hover:text-brand-green-500 hover:bg-brand-green-50/50"}`;
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300
        ${scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-glass border-b border-white/50"
          : "bg-white/50 backdrop-blur-sm"}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 flex items-center justify-between py-6 lg:py-8">
        {/* Logo */}
        <div className="flex items-center">
          <BrandIcon />
        </div>

        {/* ================= DESKTOP NAV ================= */}
        <nav className="hidden lg:flex items-center gap-2">

          {/* SERVICES */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("services")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link to="/services" className={navLinkClass("/services")}>
              Services
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openMenu === "services" ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </Link>

            <Transition
              show={openMenu === "services"}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <div className="absolute left-0 mt-2 w-72 rounded-2xl bg-white/90 backdrop-blur-xl shadow-glass-lg border border-surface-200/50 p-2">
                {Services.map((item) => (
                  <Link
                    key={item}
                    to={`/services/${slugify(item)}`}
                    className="block px-4 py-2.5 rounded-xl text-sm text-surface-800 hover:bg-brand-green-50 hover:text-brand-green-600 transition-colors"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </Transition>
          </div>

          {/* INDUSTRIES */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("industries")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link to="/industries" className={navLinkClass("/industries")}>
              Industries
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openMenu === "industries" ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </Link>

            <Transition
              show={openMenu === "industries"}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <div className="absolute left-0 mt-2 w-72 rounded-2xl bg-white/90 backdrop-blur-xl shadow-glass-lg border border-surface-200/50 p-2">
                {Industries.map((item) => (
                  <Link
                    key={item}
                    to={`/industries/${slugify(item)}`}
                    className="block px-4 py-2.5 rounded-xl text-sm text-surface-800 hover:bg-brand-green-50 hover:text-brand-green-600 transition-colors"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </Transition>
          </div>

          {/* ABOUT */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("about")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link to="/about" className={navLinkClass("/about")}>
              About
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openMenu === "about" ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </Link>

            <Transition
              show={openMenu === "about"}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <div className="absolute left-0 mt-2 w-72 rounded-2xl bg-white/90 backdrop-blur-xl shadow-glass-lg border border-surface-200/50 p-2">
                {AboutItems.map((item) => (
                  <Link
                    key={item}
                    to={`/about/${slugify(item)}`}
                    className="block px-4 py-2.5 rounded-xl text-sm text-surface-800 hover:bg-brand-green-50 hover:text-brand-green-600 transition-colors"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </Transition>
          </div>

          {/* CONTACT */}
          <Link to="/contact" className={navLinkClass("/contact", true)}>
            Contact
          </Link>

          {/* CTA */}
          <Button
            type="link"
            href="/book-consultation"
            className="btn-premium ml-5 text-base px-7 py-3"
          >
            Book a Consultation
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </nav>

        {/* ================= MOBILE BUTTON ================= */}
        <button
          type="button"
          className="lg:hidden p-2 rounded-xl text-surface-800 hover:bg-surface-100 transition"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileOpen ? (
              <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* ================= MOBILE MENU ================= */}
      {mobileOpen && (
        <div className="lg:hidden fixed top-0 left-0 w-screen h-screen z-[9999] bg-white overflow-y-auto animate-fade-in">

          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200/50">
            <BrandIcon />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-xl text-surface-800 hover:bg-surface-100 transition"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu content */}
          <div className="px-6 py-8 space-y-6">

            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block text-lg font-semibold text-surface-800 hover:text-brand-green-500 transition"
            >
              Home
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block text-lg font-semibold text-surface-800 hover:text-brand-green-500 transition"
            >
              Contact
            </Link>

            {/* SERVICES */}
            <details className="group">
              <summary className="flex justify-between items-center text-lg font-semibold text-surface-800 cursor-pointer hover:text-brand-green-500 transition">
                Services
                <svg className="w-5 h-5 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </summary>

              <div className="mt-4 space-y-3 pl-4 border-l-2 border-brand-green-200">
                {Services.map((item) => (
                  <Link
                    key={item}
                    to={`/services/${slugify(item)}`}
                    onClick={() => setMobileOpen(false)}
                    className="block text-surface-300 text-base hover:text-brand-green-500 transition"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </details>

            {/* INDUSTRIES */}
            <details className="group">
              <summary className="flex justify-between items-center text-lg font-semibold text-surface-800 cursor-pointer hover:text-brand-green-500 transition">
                Industries
                <svg className="w-5 h-5 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </summary>

              <div className="mt-4 space-y-3 pl-4 border-l-2 border-brand-green-200">
                {Industries.map((item) => (
                  <Link
                    key={item}
                    to={`/industries/${slugify(item)}`}
                    onClick={() => setMobileOpen(false)}
                    className="block text-surface-300 text-base hover:text-brand-green-500 transition"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </details>

            {/* ABOUT */}
            <details className="group">
              <summary className="flex justify-between items-center text-lg font-semibold text-surface-800 cursor-pointer hover:text-brand-green-500 transition">
                About
                <svg className="w-5 h-5 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </summary>

              <div className="mt-4 space-y-3 pl-4 border-l-2 border-brand-green-200">
                {AboutItems.map((item) => (
                  <Link
                    key={item}
                    to={`/about/${slugify(item)}`}
                    onClick={() => setMobileOpen(false)}
                    className="block text-surface-300 text-base hover:text-brand-green-500 transition"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </details>
          </div>

          {/* CTA */}
          <div className="sticky bottom-0 bg-white/90 backdrop-blur-xl border-t border-surface-200/50 px-6 py-4 flex justify-center">
            <Button
              type="link"
              href="/book-consultation"
              className="btn-premium w-full max-w-sm justify-center text-center"
              onClick={() => setMobileOpen(false)}
            >
              Book Consultation
            </Button>
          </div>

        </div>
      )}

    </header>
  );
}
